import { useState, useEffect, useCallback } from 'react';
import { AxiosError } from 'axios';
import { usersService } from '../services/users';

// ============================================================================
// Generic lazy-loading hook for user tab data
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FetchFn<T> = (...args: any[]) => Promise<{ success: boolean; data: T }>;

interface UseTabDataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  is404: boolean;
}

interface UseTabDataReturn<T> extends UseTabDataState<T> {
  refetch: () => Promise<void>;
}

function extractErrorMessage(err: unknown): { message: string; is404: boolean } {
  if (err && typeof err === 'object' && 'isAxiosError' in err) {
    const axiosErr = err as AxiosError;
    const status = axiosErr.response?.status;
    if (status === 404) {
      return { message: 'This endpoint is not yet available on the backend.', is404: true };
    }
    if (status === 500) {
      return { message: 'Server error — the backend returned 500.', is404: false };
    }
  }
  const message = err instanceof Error ? err.message : 'Failed to fetch data';
  return { message, is404: false };
}

function useTabData<T>(
  userId: number | null,
  fetchFn: (id: number) => Promise<{ success: boolean; data: T }>,
  enabled: boolean
): UseTabDataReturn<T> {
  const [state, setState] = useState<UseTabDataState<T>>({
    data: null,
    loading: false,
    error: null,
    is404: false,
  });

  const fetch = useCallback(async () => {
    if (!userId || !enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null, is404: false }));
    try {
      const response = await fetchFn(userId);
      setState({ data: response.data, loading: false, error: null, is404: false });
    } catch (err) {
      const { message, is404 } = extractErrorMessage(err);
      setState((prev) => ({ ...prev, loading: false, error: message, is404 }));
    }
  }, [userId, enabled, fetchFn]);

  useEffect(() => {
    if (enabled && userId && !state.data && !state.loading && !state.error) {
      fetch();
    }
  }, [enabled, userId, fetch]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// Specific Tab Hooks
// ============================================================================

export function useUserAvailability(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserAvailability, enabled);
}

export function useUserIdentityVerification(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserIdentityVerification, enabled);
}

export function useUserBackgroundVerification(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserBackgroundVerification, enabled);
}

export function useUserAddresses(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserAddresses, enabled);
}

export function useUserEducation(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserEducation, enabled);
}

export function useUserCertifications(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserCertifications, enabled);
}

export function useUserWorkExperience(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserWorkExperience, enabled);
}

export function useUserFollowersAndFollowing(userId: number | null, enabled: boolean) {
  const [state, setState] = useState<{
    followers: import('../types/userTabs').FollowListData | null;
    following: import('../types/userTabs').FollowListData | null;
    loading: boolean;
    error: string | null;
  }>({
    followers: null,
    following: null,
    loading: false,
    error: null,
  });

  const fetch = useCallback(async () => {
    if (!userId || !enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [followersRes, followingRes] = await Promise.all([
        usersService.getUserFollowers(userId),
        usersService.getUserFollowing(userId),
      ]);
      setState({ followers: followersRes.data, following: followingRes.data, loading: false, error: null });
    } catch (err) {
      const { message } = extractErrorMessage(err);
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId, enabled]);

  useEffect(() => {
    if (enabled && userId && !state.followers && !state.following && !state.loading && !state.error) {
      fetch();
    }
  }, [enabled, userId, fetch]);

  return { ...state, refetch: fetch };
}

export function useUserFavorites(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserFavorites, enabled);
}

export function useUserBlockedUsers(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserBlockedUsers, enabled);
}

export function useUserConnectedAccounts(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserConnectedAccounts, enabled);
}

export function useUserBadges(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserBadges, enabled);
}

export function useUserReferrals(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserReferrals, enabled);
}

export function useUserXuBux(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserXuBux, enabled);
}

export function useUserHousehold(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserHousehold, enabled);
}

export function useUserChatSummary(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserChatSummary, enabled);
}

export function useUserCalls(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserCalls, enabled);
}

export function useUserDevices(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserDevices, enabled);
}

export function useUserAuthSessions(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserAuthSessions, enabled);
}

export function useUserRecordings(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserRecordings, enabled);
}

export function useUserContentEngagement(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserContentEngagement, enabled);
}

export function useUserLocation(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserLocation, enabled);
}

export function useUserPromoRedemptions(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserPromoRedemptions, enabled);
}

export function useUserIntroVideo(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserIntroVideo, enabled);
}

export function useUserContactLists(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserContactLists, enabled);
}

export function useUserServiceRequests(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserServiceRequests, enabled);
}

export function useUserReviews(userId: number | null, enabled: boolean) {
  const [state, setState] = useState<{ data: import('../types/userTabs').ReviewsData | null; loading: boolean; error: string | null }>({
    data: null,
    loading: false,
    error: null,
  });

  const fetch = useCallback(async () => {
    if (!userId || !enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserReviews(userId, { direction: 'all', limit: 100, offset: 0 });
      setState({ data: response.data, loading: false, error: null });
    } catch (err) {
      const { message } = extractErrorMessage(err);
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId, enabled]);

  useEffect(() => {
    if (enabled && userId && !state.data && !state.loading && !state.error) {
      fetch();
    }
  }, [enabled, userId, fetch]);

  return { ...state, refetch: fetch };
}

export function useUserPayments(userId: number | null, enabled: boolean) {
  return useTabData(userId, usersService.getUserPayments, enabled);
}

export function useUserGroupSessions(userId: number | null, enabled: boolean) {
  const [state, setState] = useState<{ data: any | null; loading: boolean; error: string | null }>({
    data: null,
    loading: false,
    error: null,
  });

  const fetch = useCallback(async () => {
    if (!userId || !enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserGroupSessions(userId, { role: 'all', limit: 50, offset: 0 });
      setState({ data: response.data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch group sessions';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId, enabled]);

  useEffect(() => {
    if (enabled && userId && !state.data && !state.loading) {
      fetch();
    }
  }, [enabled, userId, fetch]);

  return { ...state, refetch: fetch };
}

// Paginated hooks for reports and notifications
export function useUserReports(userId: number | null, enabled: boolean, direction: 'made' | 'received' | 'all' = 'all') {
  const [state, setState] = useState<{ items: any[]; total: number; loading: boolean; error: string | null; hasMore: boolean }>({
    items: [],
    total: 0,
    loading: false,
    error: null,
    hasMore: false,
  });

  const fetch = useCallback(async () => {
    if (!userId || !enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserReports(userId, { direction, limit: 50, offset: 0 });
      setState({
        items: response.data.items,
        total: response.data.total,
        loading: false,
        error: null,
        hasMore: response.data.hasMore,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch reports';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId, enabled, direction]);

  useEffect(() => {
    if (enabled && userId && state.items.length === 0 && !state.loading) {
      fetch();
    }
  }, [enabled, userId, fetch]);

  return { ...state, refetch: fetch };
}

export function useUserNotifications(userId: number | null, enabled: boolean) {
  const [state, setState] = useState<{ items: any[]; total: number; loading: boolean; error: string | null; hasMore: boolean }>({
    items: [],
    total: 0,
    loading: false,
    error: null,
    hasMore: false,
  });

  const fetch = useCallback(async () => {
    if (!userId || !enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await usersService.getUserNotifications(userId, { limit: 50, offset: 0 });
      setState({
        items: response.data.items,
        total: response.data.total,
        loading: false,
        error: null,
        hasMore: response.data.hasMore,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch notifications';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [userId, enabled]);

  useEffect(() => {
    if (enabled && userId && state.items.length === 0 && !state.loading) {
      fetch();
    }
  }, [enabled, userId, fetch]);

  return { ...state, refetch: fetch };
}
